// Real-data preview for a transaction embedded inside a smart page.
//
// This is the "live preview" counterpart to EmbedPreview's structural
// mock for T+list embeds: the embed deploys the transaction's preview
// schema, lists actual rows, and supports Add/Edit/Delete via a modal
// containing the same FormRenderer the standalone Live Preview tab
// uses. Filters and selection wire into the smart-page variable system
// so chained embeds (projects → tasks → activities) work declaratively.
//
// Auto-deploy notes: each transaction the embed targets is deployed
// once per browser session (per project). Subsequent renders of the
// same obj_name skip the deploy and go straight to listing. Deploy
// state is tracked in module scope rather than React state so unmount/
// remount during normal canvas activity doesn't burn round-trips.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'
import { AlertCircle, FilePlus, Loader2, Save, X } from 'lucide-react'

import { useObjectVersion } from '@/store/objectVersionStore'

import type { ColumnDef, SqlModel, TransactionModel } from '@/pages/designers/transaction/types'
import { partitionActionsByScope } from '@/pages/designers/transaction/types'
import type { EmbedConfig, EmbedFilter, PageInput } from './types'

import {
  livePreviewDelete, livePreviewDeploy,
  livePreviewLoad, livePreviewSave,
  type ListFilter,
} from '@/livepreview/api'
import type {
  EnforcedConditionEcho, EnforcedFilterSetEcho, FilterNotice, RunActionResponse,
} from '@/livepreview/types'
import {
  embedExecute, embedList, embedLoadModel, embedResolveDefaults, embedRunAction, makeEmbedUploader,
  signInHandledByHost,
} from '@/livepreview/embedDataApi'
import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import {
  allSqlModels, auditTokenOf, calendarDefaultForInput, calendarTokenOf, columnDbName,
  inputType, isAutoGenerate, mainSqlModel, parseApiError, projectVarOf,
  visibleColumns,
} from '@/livepreview/utils'
import { applySeededItemChangeCascades } from '@/livepreview/useItemChange'
import { useGridLayout } from './embedTable/useGridLayout'
import ListView from '@/livepreview/runtime/ListView'
import FormRenderer, { fieldAnchorId } from '@/livepreview/runtime/FormRenderer'
import { FormModeBadge } from '@twasta/page-runtime/txn'

interface Props {
  project: string
  embed: EmbedConfig
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  // Called when the user selects a row in the embedded list. The page
  // designer passes a setter that updates inputValues; runtime pages
  // wire this into the PageVarsService. Either id or row can be empty
  // when their corresponding selectedIdVar / selectedDataVar is unset.
  onSetInput?: (name: string, value: unknown) => void
  /** Site-mode routing for model + CRUD (end-user published page). */
  dataAccess?: DataAccess
  /**
   * Runtime interface switcher rendered on the right of the search/filter
   * toolbar row. Supplied by EmbedListInterfaceHost so the viewer can swap
   * this list for the multi-tabular / form-input interfaces.
   */
  viewSwitcher?: React.ReactNode
  /**
   * Reports the active enforced-filter selection (set id / bypass / overrides)
   * up to EmbedListInterfaceHost so it can hand the same selection to the
   * sibling multi-tabular / form-input views — keeping the enforced filter
   * consistent when the viewer switches interfaces.
   */
  onFilterSelChange?: (sel: FilterSelection) => void
  /** Affordance gating (mode + rights). Default on so author/in-app surfaces
   * are unchanged; edit mode passes canAdd/canDelete=false. */
  canAdd?: boolean
  canDelete?: boolean
  /**
   * The block's explicit height (CSS length) from the Size & Position panel.
   * When set, the list box fills it and the grid scrolls inside; when absent the
   * box adjusts to its data up to a screen-height cap, then scrolls.
   */
  bodyHeight?: string
}

// Active enforced-filter selection shared across the interface switcher.
export interface FilterSelection {
  filterSetId?: string
  bypass: boolean
  overrides: Record<string, { from?: string; to?: string; value?: string }>
}

interface RowShape extends Record<string, unknown> {
  _txn_id: string
}

// Per-project deploy tracker. Keeps deploys idempotent across mount /
// unmount of the embed (e.g. drag-reorder reparents the React subtree).
const deployedTxns = new Map<string, Promise<void>>()

function deployKey(project: string, objName: string): string {
  return `${project}::${objName}`
}

async function ensureDeployed(project: string, objName: string, site = false): Promise<void> {
  // In site mode (public end-user page) the schema is already deployed by the
  // author; site users must not trigger a schema deploy (author-only op).
  if (site) return
  const key = deployKey(project, objName)
  const existing = deployedTxns.get(key)
  if (existing) return existing
  const p = livePreviewDeploy(project, objName).then(() => undefined)
  deployedTxns.set(key, p)
  try {
    await p
  } catch (err) {
    // Don't cache a failed deploy — let the next render retry.
    deployedTxns.delete(key)
    throw err
  }
}

// Resolve a FilterValueRef against current page-input values.
// Returns undefined when the value is unresolved (e.g. page_input
// pointing at a variable that hasn't been set yet); the backend
// converts undefined-driven filters into "no match" so chained
// selection UIs stay empty until a driving variable is chosen.
function resolveValue(
  ref: EmbedFilter['value'] | undefined,
  inputValues: Record<string, unknown>,
): unknown {
  if (!ref) return undefined
  if (ref.source === 'page_input') {
    if (!ref.name) return undefined
    const v = inputValues[ref.name]
    if (v === undefined || v === null || v === '') return undefined
    return v
  }
  // static
  const s = ref.value
  if (s === undefined || s === null || s === '') return undefined
  return s
}

// Mirror backend column_db_name normalisation (lowercase + spaces →
// underscores) so a column authored as "Customer Name" or "FirstName"
// matches the backend's main_col_set keyed on db_name.
function normalizeFilterColumn(col: string): string {
  return (col || '').trim().replace(/\s+/g, '_').toLowerCase()
}

// Filters are authored against the column's display name (c.name).
// Match through sm.columns to resolve to the normalized db_name so
// seed keys line up with FormRenderer / item_change / save payloads.
function resolveFilterKey(rawKey: string, sm: SqlModel | null): string {
  const k = (rawKey || '').trim()
  if (!k) return ''
  if (sm) {
    const lk = k.toLowerCase()
    for (const wrap of sm.columns || []) {
      const c = wrap?.column
      if (!c) continue
      if ((c.name || '').toLowerCase() === lk
          || (c.db_name || '').toLowerCase() === lk) {
        return columnDbName(c)
      }
    }
  }
  return normalizeFilterColumn(k)
}

function resolveFilters(
  filters: EmbedFilter[],
  inputValues: Record<string, unknown>,
): ListFilter[] {
  return filters
    .filter((f) => !!f.column && !!f.op)
    .map((f) => {
      const column = normalizeFilterColumn(f.column)
      if (f.op === 'between') {
        return {
          column,
          op: f.op,
          from: resolveValue(f.from, inputValues),
          to: resolveValue(f.to, inputValues),
        }
      }
      return {
        column,
        op: f.op,
        value: resolveValue(f.value, inputValues),
      }
    })
}

// Hash of resolved filter values so useEffect can detect changes
// against stringly-equal but referentially-different filter arrays.
function filtersSignature(filters: ListFilter[]): string {
  return JSON.stringify(filters)
}

export default function EmbedLivePreview({
  project, embed, inputs: _inputs, inputValues, onSetInput, dataAccess, viewSwitcher,
  onFilterSelChange, canAdd = true, canDelete = true, bodyHeight,
}: Props) {
  const objName = embed.obj_name
  const da = dataAccess
  const site = da?.mode === 'site'
  const txnVersion = useObjectVersion('T', project, objName || '')

  // ── Transaction model (column metadata for the grid/form) ──
  const [model, setModel] = useState<TransactionModel | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)
  const [modelLoading, setModelLoading] = useState(false)

  useEffect(() => {
    if (!project || !objName) {
      setModel(null)
      setModelError(null)
      return
    }
    let cancelled = false
    setModelLoading(true)
    setModelError(null)
    embedLoadModel(da, project, objName)
      .then((res) => {
        if (cancelled) return
        const content = res?.content
        if (!content) {
          setModel(null)
          setModelError(`Transaction "${objName}" not yet generated`)
          return
        }
        const m = content.transaction || content
        setModel(m as TransactionModel)
      })
      .catch(() => {
        if (cancelled) return
        setModelError('Failed to load transaction')
      })
      .finally(() => { if (!cancelled) setModelLoading(false) })
    return () => { cancelled = true }
  }, [project, objName, txnVersion, da])

  const sm = useMemo(() => (model ? mainSqlModel(model) : null), [model])

  // Live column layout (drag to resize / reorder). Author (non-site) viewers
  // can apply the change back to the transaction design.
  const mainFormNo = useMemo(
    () => (model ? (allSqlModels(model)[0]?.formNo ?? '') : ''),
    [model],
  )
  const gridLayout = useGridLayout({
    model, setModel, project, objName: objName || '', canEdit: !site,
  })

  // ── Deploy + list state ──
  const [deployState, setDeployState] = useState<
    'idle' | 'deploying' | 'deployed' | 'error'
  >('idle')
  const [deployError, setDeployError] = useState<string | null>(null)
  const [rows, setRows] = useState<RowShape[]>([])
  const [total, setTotal] = useState(0)
  const [busy, setBusy] = useState(false)
  // Server-side pagination + designer-enforced filter conditions.
  const [loadingMore, setLoadingMore] = useState(false)
  const [enforcedEcho, setEnforcedEcho] = useState<EnforcedConditionEcho[] | undefined>()
  // Designer-defined filter sets the end user can switch between (+ bypass).
  const [filterSetsEcho, setFilterSetsEcho] = useState<EnforcedFilterSetEcho[] | undefined>()
  // Non-blocking messages from the conditions' own validations.
  const [filterNotices, setFilterNotices] = useState<FilterNotice[] | undefined>()
  const [activeFilterSetId, setActiveFilterSetId] = useState<string | null>(null)
  const [bypassed, setBypassed] = useState(false)
  const filterSetIdRef = useRef<string | undefined>(undefined)
  const bypassRef = useRef(false)
  const rowsRef = useRef<RowShape[]>([])
  const pageSizeRef = useRef(50)
  const overridesRef = useRef<Record<string, { from?: string; to?: string; value?: string }>>({})
  useEffect(() => { rowsRef.current = rows }, [rows])

  // Report the current selection up so sibling interfaces stay in lockstep.
  const onFilterSelChangeRef = useRef(onFilterSelChange)
  onFilterSelChangeRef.current = onFilterSelChange
  const emitFilterSel = useCallback(() => {
    onFilterSelChangeRef.current?.({
      filterSetId: bypassRef.current ? undefined : filterSetIdRef.current,
      bypass: bypassRef.current,
      overrides: overridesRef.current,
    })
  }, [])

  const filtersResolved = useMemo(
    () => resolveFilters(embed.filters || [], inputValues),
    [embed.filters, inputValues],
  )
  const filtersSig = useMemo(
    () => filtersSignature(filtersResolved),
    [filtersResolved],
  )

  // Deploy once per (project, obj_name) per session.
  useEffect(() => {
    if (!project || !objName) return
    let cancelled = false
    setDeployState('deploying')
    setDeployError(null)
    ensureDeployed(project, objName, site)
      .then(() => {
        if (cancelled) return
        setDeployState('deployed')
      })
      .catch((err) => {
        if (cancelled) return
        const { message } = parseApiError(err)
        setDeployError(message)
        setDeployState('error')
      })
    return () => { cancelled = true }
  }, [project, objName])

  // Fetch one server page. ``append`` adds an older page below the current
  // rows ("load earlier"); otherwise it replaces them (refresh / re-range).
  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    if (!project || !objName || deployState !== 'deployed') return
    if (append) setLoadingMore(true)
    else setBusy(true)
    try {
      const res = await embedList(
        da, project, objName, pageSizeRef.current, offset,
        undefined, undefined, filtersResolved, overridesRef.current,
        { filterSetId: filterSetIdRef.current, bypass: bypassRef.current },
      )
      if (res.page_size) pageSizeRef.current = res.page_size
      setEnforcedEcho(res.enforced_filters)
      setFilterSetsEcho(res.enforced_filter_sets)
      setFilterNotices(res.filter_notices)
      setActiveFilterSetId(res.active_filter_set_id ?? null)
      setBypassed(!!res.bypassed)
      if (!bypassRef.current) filterSetIdRef.current = res.active_filter_set_id ?? undefined
      emitFilterSel()
      setTotal(res.total || 0)
      setRows((prev) => append
        ? [...prev, ...((res.rows || []) as RowShape[])]
        : ((res.rows || []) as RowShape[]))
    } catch (err) {
      if (!signInHandledByHost(da, err)) {
        const { message } = parseApiError(err)
        toast.error(`Could not load ${objName}: ${message}`)
      }
      if (!append) { setRows([]); setTotal(0) }
    } finally {
      if (append) setLoadingMore(false)
      else setBusy(false)
    }
  }, [project, objName, deployState, da, filtersResolved, emitFilterSel])

  // Refetch list whenever filters or deploy state changes.
  const refreshList = useCallback(async () => {
    await fetchPage(0, false)
  }, [fetchPage])

  const loadEarlier = useCallback(async () => {
    await fetchPage(rowsRef.current.length, true)
  }, [fetchPage])

  // End user changes the enforced conditions' values (locked ones are ignored
  // server-side); reset to the newest page.
  const applyFilters = useCallback(async (
    overrides: Record<string, { from?: string; to?: string; value?: string }>,
  ) => {
    overridesRef.current = overrides
    await fetchPage(0, false)
  }, [fetchPage])

  // User switched the active filter set (or chose "No filter"). Reset the
  // per-condition overrides — the new set's defaults come from its echo.
  const selectFilterSet = useCallback(async (
    sel: { setId?: string; bypass?: boolean },
  ) => {
    bypassRef.current = !!sel.bypass
    filterSetIdRef.current = sel.bypass ? undefined : sel.setId
    overridesRef.current = {}
    await fetchPage(0, false)
  }, [fetchPage])

  useEffect(() => {
    if (deployState !== 'deployed') return
    refreshList()
    // filtersSig participates so any change to resolved filter values
    // (typically driven by upstream selection) triggers a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, deployState, filtersSig])

  // ── Editor modal state ──
  const [editorOpen, setEditorOpen] = useState(false)
  const [editTxnId, setEditTxnId] = useState<string | null>(null)
  // Record locked (engine edit-lock / awaiting approval) or a 'view' system
  // action was invoked → open read-only with a View indicator, hide Save.
  const [viewOnly, setViewOnly] = useState(false)
  const [lockReason, setLockReason] = useState('')
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formBanner, setFormBanner] = useState<string | null>(null)
  const [formDirty, setFormDirty] = useState(false)

  // ── Selection plumbing ──
  // We track the currently-selected _txn_id locally so the row gets a
  // highlight; the values are also forwarded to the page's variables
  // via onSetInput when configured.
  const [selectedTxnId, setSelectedTxnId] = useState<string | null>(null)

  // Stale-callback guard: writing the same value into onSetInput on
  // every render would create a render loop. Compare with the last
  // value we wrote.
  const lastSelectionRef = useRef<{ id: string | null; data: RowShape | null }>({
    id: null, data: null,
  })

  const writeSelectionVars = useCallback((row: RowShape | null) => {
    if (!onSetInput) return
    const id = row?._txn_id ?? null
    const last = lastSelectionRef.current
    if (embed.selectedIdVar && last.id !== id) {
      onSetInput(embed.selectedIdVar, id ?? '')
    }
    if (embed.selectedDataVar && last.data !== row) {
      onSetInput(embed.selectedDataVar, row || undefined)
    }
    lastSelectionRef.current = { id, data: row }
  }, [embed.selectedIdVar, embed.selectedDataVar, onSetInput])

  const handleSelect = useCallback((row: RowShape) => {
    setSelectedTxnId(row._txn_id)
    writeSelectionVars(row)
  }, [writeSelectionVars])

  // When the embed's filters cause the previously-selected row to fall
  // out of the visible set, clear the selection so downstream embeds
  // don't keep showing data for an invisible parent.
  useEffect(() => {
    if (selectedTxnId == null) return
    const still = rows.find(r => r._txn_id === selectedTxnId)
    if (!still) {
      setSelectedTxnId(null)
      writeSelectionVars(null)
    }
  }, [rows, selectedTxnId, writeSelectionVars])

  // Restore the selection from `inputValues[embed.selectedIdVar]` when
  // that variable carries a value the embed didn't write itself (e.g.
  // a smart document that was reopened — the saved vars rehydrate
  // before the embed loads its rows). Re-syncs to whatever upstream
  // wrote the var so chained embeds keep working. Seeds
  // `lastSelectionRef` directly so writeSelectionVars stays a no-op
  // on the echo and doesn't trigger a pointless round-trip save.
  useEffect(() => {
    const varName = embed.selectedIdVar
    if (!varName) return
    const raw = inputValues[varName]
    const target = raw == null || raw === '' ? null : String(raw)
    if (target === selectedTxnId) return
    if (target == null) {
      // Upstream cleared the var — drop local selection too so the
      // highlight tracks the truth.
      setSelectedTxnId(null)
      lastSelectionRef.current = { id: null, data: null }
      return
    }
    // Wait for the matching row to appear in the loaded set. If the
    // saved id no longer exists (e.g. row was deleted between sessions),
    // leave the local selection unset rather than highlighting nothing.
    const row = rows.find(r => r._txn_id === target)
    if (!row) return
    setSelectedTxnId(target)
    lastSelectionRef.current = { id: target, data: row }
  }, [embed.selectedIdVar, inputValues, rows, selectedTxnId])

  // ── CRUD: Add / Edit / Delete ──

  const startAdd = useCallback(async () => {
    if (!sm) return
    setEditTxnId(null)
    setViewOnly(false)
    setLockReason('')
    setFieldErrors({})
    setFormBanner(null)
    let initial: Record<string, unknown> = {}
    for (const col of visibleColumns(sm)) {
      const dv = (col.default_value || '').trim()
      if (!dv) continue
      if (auditTokenOf(col) || projectVarOf(col) || isAutoGenerate(col)) continue
      // Calendar tokens (`:calendar_today`, etc.) — resolve client-side
      // so the form shows the concrete date and the value posts as a
      // valid yyyy-MM-dd instead of the raw token text.
      if (calendarTokenOf(col)) {
        const calVal = calendarDefaultForInput(dv, inputType(col))
        if (calVal) initial[columnDbName(col)] = calVal
        continue
      }
      initial[columnDbName(col)] = dv
    }
    // Seed filter-driven columns so e.g. adding a task to a selected
    // project pre-fills the FK column from the selected_project_id var.
    // Filter columns are stored as the column's display name (c.name);
    // resolve through sm.columns to the normalized db_name so the seed
    // key matches what FormRenderer reads and what the item_change
    // cascade scans below.
    for (const f of (embed.filters || [])) {
      if (f.op !== 'eq') continue
      const v = resolveValue(f.value, inputValues)
      if (v === undefined) continue
      const key = resolveFilterKey(f.column || '', sm)
      if (key) initial[key] = v
    }
    setFormValues(initial)
    setFormDirty(false)
    setEditorOpen(true)
    try {
      const res = await embedResolveDefaults(da, project, objName)
      if (res?.values && Object.keys(res.values).length > 0) {
        initial = { ...res.values, ...initial }
        setFormValues(initial)
      }
    } catch {
      // Non-fatal — manual defaults still apply.
    }
    // Cascade item_change for every column that was seeded from a
    // filter / arg / default so dependent columns (e.g. project_name
    // driven by project_code's lookup rule) populate without the user
    // having to retype the trigger. Best-effort — the helper swallows
    // network / server failures internally.
    try {
      const cascaded = await applySeededItemChangeCascades(
        da,
        project, objName, sm, initial,
      )
      setFormValues(cascaded)
    } catch {
      // Non-fatal — user can still edit.
    }
  }, [sm, project, objName, embed.filters, inputValues])

  const startEdit = useCallback(async (txnId: string, opts?: { forceView?: boolean }) => {
    if (!sm) return
    setBusy(true)
    setFieldErrors({})
    setFormBanner(null)
    try {
      const res = site
        ? (await embedExecute(da, project, objName, 'load', { txn_id: txnId })) as Awaited<ReturnType<typeof livePreviewLoad>>
        : await livePreviewLoad(project, objName, txnId)
      const header = { ...res.header } as Record<string, unknown>
      delete header._txn_id
      setFormValues(header)
      setEditTxnId(txnId)
      // Open read-only when the engine reports the record locked, or when a
      // 'view' system action forced it (the edit conditions don't run in view).
      const ro = !!opts?.forceView || res.editable === false
      setViewOnly(ro)
      setLockReason(ro ? (res.lock_reason || '') : '')
      setFormDirty(false)
      setEditorOpen(true)
    } catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Could not load record: ${message}`)
    } finally {
      setBusy(false)
    }
  }, [project, objName, sm, site, da])

  const closeEditor = useCallback(() => {
    if (formDirty && !window.confirm(
      'You have unsaved changes. Discard them?',
    )) return
    setEditorOpen(false)
    setEditTxnId(null)
    setViewOnly(false)
    setLockReason('')
    setFormValues({})
    setFieldErrors({})
    setFormBanner(null)
    setFormDirty(false)
  }, [formDirty])

  const saveForm = useCallback(async (close: boolean) => {
    setBusy(true)
    setFieldErrors({})
    setFormBanner(null)
    try {
      const res = site
        ? (await embedExecute(da, project, objName, 'save', { header: formValues, txn_id: editTxnId || undefined })) as Awaited<ReturnType<typeof livePreviewSave>>
        : await livePreviewSave(
        project, objName, formValues, editTxnId || undefined,
      )
      toast.success(editTxnId ? 'Record updated' : 'Record added')
      if (close) {
        setEditorOpen(false)
        setEditTxnId(null)
        setFormValues({})
        setFormDirty(false)
      } else {
        if (res.action === 'add') setEditTxnId(res.txn_id)
        if (res.header) {
          const saved = { ...res.header } as Record<string, unknown>
          delete saved._txn_id
          setFormValues(saved)
        }
        setFormDirty(false)
      }
      await refreshList()
    } catch (err) {
      const { message, validationErrors } = parseApiError(err)
      if (validationErrors.length > 0) {
        const fmap: Record<string, string> = {}
        const general: string[] = []
        for (const it of validationErrors) {
          if (it.field && it.field !== '_record') fmap[it.field] = it.message
          else general.push(it.message)
        }
        setFieldErrors(fmap)
        setFormBanner(general.length > 0 ? general.join(' · ') : null)
        toast.error(general[0] || Object.values(fmap)[0] || message)
        const first = Object.keys(fmap)[0]
        if (first) {
          window.setTimeout(() => {
            const el = document.getElementById(fieldAnchorId(first))
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' })
              const input = el.querySelector('input, select, textarea') as HTMLElement | null
              if (input) input.focus()
            }
          }, 50)
        }
      } else {
        setFormBanner(message)
        toast.error(message)
      }
    } finally {
      setBusy(false)
    }
  }, [project, objName, formValues, editTxnId, refreshList])

  const deleteRow = useCallback(async (txnId: string) => {
    if (!window.confirm('Delete this record?')) return
    setBusy(true)
    try {
      if (site) await embedExecute(da, project, objName, 'delete', { txn_id: txnId })
      else await livePreviewDelete(project, objName, txnId)
      toast.success('Record deleted')
      if (selectedTxnId === txnId) {
        setSelectedTxnId(null)
        writeSelectionVars(null)
      }
      if (editTxnId === txnId) {
        setEditTxnId(null)
        setEditorOpen(false)
        setFormValues({})
        setFormDirty(false)
      }
      await refreshList()
    } catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Delete failed: ${message}`)
    } finally {
      setBusy(false)
    }
  }, [project, objName, refreshList, editTxnId, selectedTxnId, writeSelectionVars])

  const onFieldChange = useCallback((name: string, v: unknown) => {
    setFormValues(prev => ({ ...prev, [name]: v }))
    setFormDirty(true)
  }, [])

  // ── Custom action buttons ─────────────────────────────────────────────
  const runActionTransport = useCallback(
    (p: string, o: string, req: Parameters<typeof embedRunAction>[3]) =>
      embedRunAction(da, p, o, req),
    [da],
  )

  const applyActionResult = useCallback((res: RunActionResponse) => {
    const headerNo = sm?.form_no || '1'
    if (res.handler === 'select_grid') {
      const rows = res.rows || []
      if (rows.length) setFormValues(prev => ({ ...prev, ...rows[0] }))
      setFormDirty(true)
      return
    }
    const set = { ...(res.updates || {}), ...(res.forms?.[headerNo]?.set || {}) }
    if (Object.keys(set).length) {
      setFormValues(prev => ({ ...prev, ...set }))
      setFormDirty(true)
    }
  }, [sm])

  const handleSystemAction = useCallback((op: 'add' | 'edit' | 'delete' | 'save' | 'view') => {
    if (op === 'save') void saveForm(false)
    else if (op === 'add') void startAdd()
    else if (op === 'delete' && editTxnId) void deleteRow(editTxnId)
  }, [saveForm, startAdd, deleteRow, editTxnId])

  const handleActionError = useCallback((err: unknown) => {
    const { message, validationErrors } = parseApiError(err)
    if (validationErrors.length > 0) {
      const fmap: Record<string, string> = {}
      const general: string[] = []
      for (const it of validationErrors) {
        if (it.field && it.field !== '_record') fmap[it.field] = it.message
        else general.push(it.message)
      }
      setFieldErrors(fmap)
      setFormBanner(general.length > 0 ? general.join(' · ') : null)
      toast.error(general[0] || Object.values(fmap)[0] || message)
    } else {
      setFormBanner(message)
      toast.error(message)
    }
  }, [])

  // ── Merged system actions (add / edit) ────────────────────────────────
  // A system 'add' / 'edit' action defined on the transaction merges into
  // the built-in Add button / Edit control (label / hover text) instead of
  // rendering as a separate button; the click routes through run_action so
  // the action's pre-action validations gate the operation. Same contract
  // as the standalone Live Preview list (see partitionActionsByScope).
  const { systemOverrides } = useMemo(
    () => partitionActionsByScope(sm?.actions), [sm])

  const runMergedSystemAction = useCallback(async (
    name: string,
    target: { mode: 'add' } | { mode: 'edit'; txnId: string },
  ) => {
    setBusy(true)
    try {
      const row = target.mode === 'edit'
        ? rows.find(r => r._txn_id === target.txnId)
        : undefined
      const res = await runActionTransport(project, objName, {
        formNo: sm?.form_no || '1',
        actionName: name,
        header: (row as Record<string, unknown> | undefined) || {},
        ...(target.mode === 'edit' ? { txnId: target.txnId } : {}),
      })
      res.prompts?.forEach(p => toast(p.message, { icon: 'ℹ️' }))
      if (res.handler === 'system') {
        if (res.system_op === 'add') await startAdd()
        // A 'view' system action opens the record read-only after ITS
        // conditions pass — the edit action's conditions never run for view.
        else if (res.system_op === 'view' && target.mode === 'edit') await startEdit(target.txnId, { forceView: true })
        else if (res.system_op === 'edit' && target.mode === 'edit') await startEdit(target.txnId)
        else if (res.system_op === 'delete' && target.mode === 'edit') await deleteRow(target.txnId)
      }
    } catch (err) {
      // The action ran from the list (no editor open) — surface the
      // blocking validation as a toast.
      const { message, validationErrors } = parseApiError(err)
      toast.error(validationErrors[0]?.message || message || 'Action failed')
    } finally {
      setBusy(false)
    }
  }, [project, objName, sm, rows, runActionTransport, startAdd, startEdit, deleteRow])

  // ── Render ──

  if (!objName) {
    return (
      <div style={emptyStyle}>
        <AlertCircle size={18} />
        <span>Pick a transaction in the property panel to embed.</span>
      </div>
    )
  }
  if (modelLoading) {
    return (
      <div style={emptyStyle}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading transaction model…</span>
      </div>
    )
  }
  if (modelError) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{modelError}</span>
      </div>
    )
  }
  if (!sm) return null

  // No identity banner — the embed mode ("Live list") and object name are
  // already shown in the side panel when the block is selected, so a header
  // row here just duplicates them. Surface only the transient deploy/sync
  // status, and only while it's actionable (deploying or failed), so the
  // settled state adds no extra line.
  const statusStrip = (deployState === 'deploying' || deployState === 'error') ? (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', fontSize: '0.8125rem',
      color: deployState === 'error' ? 'var(--color-error)' : 'var(--color-text-secondary)',
    }}>
      {deployState === 'deploying' && (
        <>
          <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
          deploying…
        </>
      )}
      {deployState === 'error' && <>deploy failed: {deployError}</>}
    </div>
  ) : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {statusStrip}
      <div style={{
        border: '1px solid var(--color-border)', borderRadius: 8,
        background: 'var(--color-bg)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        // Height model (ListView is a `flex:1, minHeight:0` child, so it fills
        // and scrolls its grid internally — toolbar/header stay put):
        // • explicit block height → fill it.
        // • no explicit height → grow with the data up to one screen, then the
        //   grid scrolls. The flex max-height clamp is what makes the inner grid
        //   scroll without pushing the rest of the page.
        ...(bodyHeight ? { height: bodyHeight } : { maxHeight: '80vh' }),
      }}>
        <ListView
          model={sm}
          rows={rows}
          total={total}
          busy={busy || deployState !== 'deployed'}
          gridLayout={gridLayout}
          formNo={mainFormNo}
          selectedTxnId={selectedTxnId}
          gridUx={embed.gridUx}
          columnTotals={embed.columnTotals}
          viewSwitcher={viewSwitcher}
          canAdd={canAdd}
          canDelete={canDelete}
          onAdd={startAdd}
          // Single click → bind selection variables only (drives any
          // chained embed filters + `{{selected.*}}` text bindings).
          // Double click triggers onEdit below to actually open the
          // editor modal. This split mirrors how OS file managers
          // separate select vs open, and avoids the surprise where
          // simply clicking a row in a master list yanked the user
          // into an edit form.
          onSelect={handleSelect}
          onEdit={(id) => {
            // Double-click path: keep the selection in sync (so the
            // editor + downstream bindings agree on which row is
            // active), then open the editor modal.
            const row = rows.find(r => r._txn_id === id)
            if (row) handleSelect(row)
            startEdit(id)
          }}
          // Merged system actions: relabel the built-in Add / retitle the
          // Edit pencil and gate both behind the action's pre-action
          // validations (no custom action buttons render on this surface).
          systemOverrides={systemOverrides}
          onRunFormAction={(name) => void runMergedSystemAction(name, { mode: 'add' })}
          onRunRowAction={(name, id) => {
            // Mirror the onEdit path: sync the selection before the gate.
            const row = rows.find(r => r._txn_id === id)
            if (row) handleSelect(row)
            void runMergedSystemAction(name, { mode: 'edit', txnId: id })
          }}
          // A system Delete action merges into the built-in Delete: the
          // delete runs through it so its pre-action validations gate it.
          onDelete={(id) => systemOverrides.delete
            ? void runMergedSystemAction(systemOverrides.delete.name, { mode: 'edit', txnId: id })
            : void deleteRow(id)}
          onRefresh={refreshList}
          enforcedFilters={enforcedEcho}
          onApplyFilters={applyFilters}
          filterSets={filterSetsEcho}
          activeFilterSetId={activeFilterSetId}
          bypassed={bypassed}
          onSelectFilterSet={selectFilterSet}
          filterNotices={filterNotices}
          project={project}
          objName={objName}
          pagination={{
            loadedCount: rows.length,
            total,
            hasMore: rows.length < total,
            loadingMore,
            onLoadEarlier: loadEarlier,
          }}
        />
      </div>

      <EditorModal
        open={editorOpen}
        title={viewOnly ? `View ${sm.form_title || objName}` : editTxnId ? `Edit ${sm.form_title || objName}` : `New ${sm.form_title || objName}`}
        txnId={editTxnId}
        dirty={formDirty}
        busy={busy}
        viewOnly={viewOnly}
        lockReason={lockReason}
        onRequestClose={closeEditor}
        onSave={() => saveForm(false)}
        onSaveAndClose={() => saveForm(true)}
      >
        <ValidationBanner
          fieldErrors={fieldErrors}
          banner={formBanner}
          columnLabel={(dbName) => columnLabelFromSm(sm, dbName)}
        />
        <div style={{ flex: 1, overflow: 'auto' }}>
          <FormRenderer
            model={sm}
            values={formValues}
            errors={fieldErrors}
            onChange={onFieldChange}
            readOnlyKey={!!editTxnId}
            viewOnly={viewOnly}
            lockReason={lockReason}
            project={project}
            objName={objName}
            uploadDocument={makeEmbedUploader(dataAccess, project)}
            runAction={runActionTransport}
            formNo={sm.form_no}
            txnId={editTxnId || undefined}
            onActionResult={applyActionResult}
            onSystemAction={handleSystemAction}
            onActionError={handleActionError}
          />
        </div>
      </EditorModal>
    </div>
  )
}

// ─── Local modal helper (slimmer than LivePreviewTab's; no fullscreen) ───

function EditorModal({
  open, title, txnId, dirty, busy, viewOnly, lockReason,
  onRequestClose, onSave, onSaveAndClose, children,
}: {
  open: boolean
  title: string
  txnId: string | null
  dirty: boolean
  busy: boolean
  // Read-only record: hide the Save controls.
  viewOnly?: boolean
  // Why the record is read-only — shown as the mode badge's hover tooltip.
  lockReason?: string
  onRequestClose: () => void
  onSave: () => void
  onSaveAndClose: () => void
  children: React.ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onRequestClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onRequestClose])

  if (!open) return null
  const node = (
    <div
      onClick={onRequestClose}
      style={{
        // Above the Run view's sticky top bar (zIndex 9000) so the editor
        // header isn't truncated behind it — matches the other edit modals
        // (LivePreviewTab / EmbedCapturePreview at 9430).
        position: 'fixed', inset: 0, zIndex: 9430,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          width: 'min(1000px, 96vw)',
          height: 'min(760px, 92vh)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 18px',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ fontSize: '0.9625rem' }}>{title}</strong>
            <FormModeBadge
              mode={viewOnly ? 'view' : txnId ? 'edit' : 'add'}
              lockReason={lockReason}
            />
            {txnId && (
              <code style={{
                fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                background: 'var(--color-bg-secondary)',
                padding: '2px 6px', borderRadius: 3,
              }}>
                {txnId.slice(0, 18)}{txnId.length > 18 ? '…' : ''}
              </code>
            )}
            {dirty && (
              <span style={{
                fontSize: '0.8125rem', color: '#eab308', fontWeight: 600,
              }}>● unsaved</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={onRequestClose} disabled={busy}>
              <X size={14} /> Close
            </button>
            {!viewOnly && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={onSave} disabled={busy}>
                  <Save size={14} /> Save
                </button>
                <button className="btn btn-primary btn-sm" onClick={onSaveAndClose} disabled={busy}>
                  <FilePlus size={14} /> Save &amp; Close
                </button>
              </>
            )}
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      </div>
    </div>
  )

  return createPortal(node, document.body)
}

function ValidationBanner({
  fieldErrors, banner, columnLabel,
}: {
  fieldErrors: Record<string, string>
  banner: string | null
  columnLabel: (dbName: string) => string
}) {
  const keys = Object.keys(fieldErrors)
  if (keys.length === 0 && !banner) return null
  return (
    <div style={{
      padding: '10px 16px',
      background: 'rgba(239,68,68,0.08)',
      borderBottom: '1px solid rgba(239,68,68,0.35)',
      color: '#ef4444',
      fontSize: '0.8425rem',
    }}>
      <div style={{ fontWeight: 600, marginBottom: keys.length ? 4 : 0 }}>
        {keys.length > 0
          ? `Save blocked — ${keys.length} validation error${keys.length === 1 ? '' : 's'}`
          : banner}
      </div>
      {keys.length > 0 && (
        <ul style={{
          listStyle: 'none', padding: 0, margin: 0,
          display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          {keys.map(k => (
            <li key={k}><strong>{columnLabel(k)}</strong>: {fieldErrors[k]}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function columnLabelFromSm(sm: SqlModel | null, dbName: string): string {
  if (!sm?.columns) return dbName
  for (const wrap of sm.columns) {
    const col = wrap.column as ColumnDef | undefined
    if (!col) continue
    const dbn = (col.db_name || col.name || '').replace(/\s+/g, '_').toLowerCase()
    if (dbn === dbName) return col.heading || col.name || dbName
  }
  return dbName
}

const emptyStyle: React.CSSProperties = {
  padding: '20px 14px',
  display: 'flex', alignItems: 'center', gap: 10,
  background: 'var(--color-bg)',
  border: '1px dashed var(--color-border)',
  borderRadius: 8,
  color: 'var(--color-text-secondary)',
  fontSize: '0.9125rem',
}
